// Tempatkan di: src/components/PdfMemoKirim/PdfMemoKirim.jsx
import React from "react";
import { Page, Text, View, Document } from "@react-pdf/renderer";
import moment from "moment";
import { bulan } from "../../util/constant";
import {
  formatDot,
  switchDotsAndCommas,
  numberWithCommas,
} from "../../util/utilFunction";

const fmt = (n) => formatDot(switchDotsAndCommas(numberWithCommas(n)));

/* ---------- keterangan di bawah tabel (daftar customer) ---------- */
const dataCustomer = [
  { customer: "PT.CENTURY BATTERY I", initial: "CBI" },
  { customer: "PT.GS BATTERY SUNTER", initial: "GSB" },
  { customer: "PT.GS BATTERY KERAWANG", initial: "GSK" },
  { customer: "PT.TRIMITRA BATERAI PERKASA", initial: "TBP" },
  { customer: "PT.YUASA BATTERY IND", initial: "YBI" },
  { customer: "PT.COMPOTEC INDONESIA", initial: "CI" },
];

/* ---------- urutan & aturan tiap customer di tabel ----------
   catatan : kunci catatan yang ditampilkan di atas barisnya (null = tanpa catatan)
   showKode: nama barang diawali kode barang (TBP, YBI)
   cycle   : warna sel No. Polisi menurut CYCLE1/2/3 (GS, GSS)                       */
const SECTIONS = [
  { key: "TIM", catatan: "TIM" },
  { key: "COMPOTEC", catatan: "COMPOTEC" },
  { key: "CBI", catatan: "CBI" },
  { key: "GS", catatan: "GS", cycle: true },
  { key: "GSS", catatan: null, cycle: true },
  { key: "TBP", catatan: "TBP", showKode: true },
  { key: "YBI", catatan: "YBI", showKode: true },
  { key: "MELCO", catatan: "MELCO" },
];

/* ---------- lebar kolom (persen dari lebar tabel penuh) ---------- */
const COL = {
  no: 3,
  item: 30,
  cus: 4,
  pack: 4,
  unit: 5,
  plus: 2,
  receh: 6,
  pcs: 6,
  po: 13,
  line: 10,
  kode: 6,
  check: 4,
  pol: 7,
};
// Kolom No. Polisi digabung per grup, jadi kolom lain berada di dalam wadah selebar (100 - pol)%
const LEFT = 100 - COL.pol;
const w = (pct) => `${((pct / LEFT) * 100).toFixed(4)}%`;

const BORDER = "1px solid black";
const ROW_H = "18px";

const cell = (pct, extra = {}) => ({
  fontSize: "7px",
  textAlign: "center",
  borderRight: BORDER,
  borderBottom: BORDER,
  width: w(pct),
  padding: "4px 2px 0 2px",
  height: ROW_H,
  ...extra,
});

/* ---------- satu baris barang (tanpa kolom No. Polisi) ---------- */
const renderItemRow = (obj, number, section, key) => {
  const isi = obj.isiKemasan;
  const full = obj.jumlah / isi;
  const receh = obj.jumlah - Math.floor(full) * isi;
  const pel = obj.isPelengkap;

  return (
    <View
      key={key}
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        ...(obj.isBatal ? { backgroundColor: "#d10000", color: "white" } : {}),
      }}
    >
      <Text style={cell(COL.no, { borderLeft: BORDER })}>{number}</Text>
      <Text
        style={cell(COL.item, {
          textAlign: "left",
          padding: "1px 2px 0 2px",
        })}
      >
        {section.showKode ? `${obj.kodeBarang} ${obj.itemPo}` : obj.itemPo}
      </Text>
      <Text style={cell(COL.cus)}>{obj.customer}</Text>
      <Text style={cell(COL.pack)}>{pel ? "" : Math.floor(full)}</Text>
      <Text style={cell(COL.unit)}>{pel ? "" : obj.satuan}</Text>
      <Text style={cell(COL.plus)}>+</Text>
      <Text style={cell(COL.receh)}>{pel ? "" : fmt(receh)}</Text>
      <Text style={cell(COL.pcs)}>{fmt(obj.jumlah)}</Text>
      <Text style={cell(COL.po)}>{obj.noPo ? `NO.PO.${obj.noPo}` : ""}</Text>
      <Text style={cell(COL.line, { fontSize: "6px" })}>
        {obj.noLine
          ? `NO.LINE:${obj.noLine}`
          : obj.noSkk
          ? `SKK:${obj.noSkk}`
          : ""}
      </Text>
      <Text
        style={cell(COL.kode, { fontSize: "6px", padding: "4px 2px 0 1px" })}
      >
        {obj.kodeLine}
      </Text>
      <Text
        style={cell(COL.check, {
          fontSize: "6px",
          fontStyle: "italic",
          padding: "1px 2px 0 2px",
        })}
      />
    </View>
  );
};

/* ---------- kelompokkan baris berurutan dengan No. Polisi yang sama ---------- */
const labelOf = (o) =>
  o.isPelengkap ? "PELENGKAP" : String(o.noPol || "").trim();

const groupRows = (list) => {
  const groups = [];
  list.forEach((o) => {
    const label = labelOf(o);
    const last = groups[groups.length - 1];
    if (last && label && last.label === label) last.items.push(o);
    else groups.push({ label, items: [o] });
  });
  return groups;
};

const polStyle = (label, allBatal, cycle) => {
  if (allBatal) return { backgroundColor: "#d10000", color: "white" };
  if (cycle && label.includes("CYCLE2")) return { backgroundColor: "gray" };
  if (cycle && label.includes("CYCLE3"))
    return { backgroundColor: "black", color: "white" };
  return {};
};

/* ---------- satu customer: catatan, grup baris, garis penutup ---------- */
const renderSection = (section, listData, listCatatan) => {
  const rows = listData.filter((o) => o.customer === section.key);
  const cat = section.catatan
    ? (listCatatan || []).find((i) => i.company === section.catatan)
    : null;
  const catatanRows = (cat && cat.data) || [];

  let counter = 0;

  return (
    <React.Fragment key={section.key}>
      {catatanRows.map((ele, i) => (
        <View
          key={`c${i}`}
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text
            style={{
              fontSize: "12px",
              textAlign: "center",
              border: BORDER,
              width: "100%",
              fontWeight: "bold",
              padding: "2px 2px 0 2px",
              height: ROW_H,
              borderTop: "none",
            }}
          >
            {ele.desc}
          </Text>
        </View>
      ))}

      {groupRows(rows).map((g, gi) => {
        const allBatal = g.items.every((o) => o.isBatal);
        const items = g.items.map((o) => {
          counter += 1;
          return renderItemRow(
            o,
            counter,
            section,
            `${section.key}-${counter}`
          );
        });
        return (
          <View key={`g${gi}`} wrap={false} style={{ flexDirection: "row" }}>
            <View style={{ width: `${LEFT}%` }}>{items}</View>
            {/* sel No. Polisi digabung setinggi seluruh baris di grupnya */}
            <View
              style={{
                width: `${COL.pol}%`,
                borderRight: BORDER,
                borderBottom: BORDER,
                alignItems: "center",
                justifyContent: "center",
                ...polStyle(g.label, allBatal, section.cycle),
              }}
            >
              <Text
                style={{
                  fontSize: g.label.length > 8 ? "4.8px" : "7px", // label panjang (PELENGKAP) agar tidak terpotong
                  fontWeight: "bold",
                  textAlign: "center",
                  padding: "0 1px",
                }}
              >
                {g.label}
              </Text>
            </View>
          </View>
        );
      })}

      {rows.length > 0 ? (
        <View>
          <Text style={{ borderBottom: BORDER }}></Text>
        </View>
      ) : (
        <View />
      )}
    </React.Fragment>
  );
};

/* ---------- header kolom tabel ---------- */
const headCell = (pct, label, extra = {}) => (
  <Text
    style={{
      fontSize: "7px",
      textAlign: "center",
      borderTop: BORDER,
      borderRight: BORDER,
      borderBottom: BORDER,
      width: `${pct}%`,
      padding: "6px 0 0 0",
      height: "20px",
      ...extra,
    }}
  >
    {label}
  </Text>
);

const PdfMemoKirim = (props) => {
  // baris jumlah 0 = SJ yang dibatalkan penuh (catatan riwayat), tidak ikut dicetak
  const listData = [...(props.data || [])].filter(
    (e) => e.isBatal || Number(e.jumlah) > 0
  );
  const listCatatan = props.catatan;

  const month = moment(props.date).utcOffset("+0700").month() + 1;
  const findBulan = bulan.find((i) => Number(i.value) === month);

  return (
    <Document>
      <Page size="A4" style={{ padding: "12px" }}>
        <>
          <View style={{ flexDirection: "row", width: "100%" }}>
            <Text
              style={{
                border: BORDER,
                fontWeight: "bold",
                textAlign: "center",
                fontSize: "12px",
                width: "8%",
                padding: "2px 0 0 0",
              }}
            >
              TIM
            </Text>
            <Text
              style={{
                borderTop: BORDER,
                borderRight: BORDER,
                borderBottom: BORDER,
                fontSize: "12px",
                fontWeight: "bold",
                width: "25%",
                textAlign: "center",
                padding: "2px 0 0 0",
              }}
            >
              MEMO PENGIRIMAN
            </Text>
            <Text
              style={{
                borderTop: BORDER,
                borderRight: BORDER,
                borderBottom: BORDER,
                fontSize: "12px",
                width: "67%",
                textAlign: "center",
                padding: "2px 0 0 0",
                backgroundColor: "yellow",
              }}
            >
              TANGGAL PACKING HARUS DIATAS TANGGAL{" "}
              <Text style={{ fontWeight: "bold" }}>
                {moment(props.date)
                  .utcOffset("+0700")
                  .subtract(87, "days")
                  .format("DD-MM-YYYY")}{" "}
              </Text>
            </Text>
          </View>

          <View style={{ flexDirection: "row", marginBottom: "1px" }}>
            <Text
              style={{
                border: BORDER,
                width: "33%",
                fontSize: "10px",
                fontWeight: "bold",
                alignContent: "center",
                padding: "2px 0 0 4px",
              }}
            >
              TGL/BLN/TH.
            </Text>
            <View
              style={{
                borderTop: BORDER,
                borderRight: BORDER,
                borderBottom: BORDER,
                flexDirection: "row",
                justifyContent: "space-between",
                fontSize: "10px",
                fontWeight: "bold",
                width: "27%",
                padding: "2px 4px 0 4px",
              }}
            >
              <Text>{moment(props.date).utcOffset("+0700").format("DD")}</Text>
              <Text>{findBulan ? findBulan.name.toUpperCase() : ""}</Text>
              <Text>
                {moment(props.date).utcOffset("+0700").format("YYYY")}
              </Text>
            </View>
            <Text
              style={{
                borderTop: BORDER,
                borderRight: BORDER,
                borderBottom: BORDER,
                fontSize: "10px",
                padding: "2px 0 0 0",
                width: "13%",
                textAlign: "center",
              }}
            >
              REV
            </Text>
            <Text
              style={{
                borderTop: BORDER,
                borderRight: BORDER,
                borderBottom: BORDER,
                fontSize: "10px",
                padding: "2px 0 0 0",
                textAlign: "center",
                width: "16%",
              }}
            >
              Form-EXP-A1-R1
            </Text>
          </View>

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {headCell(3, "NO", { border: BORDER })}
            {headCell(30, "NAMA BARANG/TYPE/WARNA")}
            {headCell(4, "CUS")}
            {headCell(17, "P.KEMASAN")}
            {headCell(6, "JML PCS")}
            {headCell(13, "NO.PO")}
            {headCell(16, "KETERANGAN")}
            {headCell(4, "CHECK BY WH", {
              fontSize: "6px",
              fontStyle: "italic",
              padding: "2px 0 0 0",
            })}
            {headCell(7, "NO. POLISI", { fontSize: "6px" })}
          </View>

          {SECTIONS.map((s) => renderSection(s, listData, listCatatan))}
        </>

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginTop: "6px",
          }}
        >
          <Text
            style={{
              fontSize: "6px",
              textAlign: "center",
              borderRight: BORDER,
              borderLeft: BORDER,
              borderTop: BORDER,
              width: "3%",
              padding: "2px 0 0 2px",
            }}
          >
            KET:
          </Text>
        </View>
        <View
          style={{
            flexDirection: "row",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <View style={{ width: "50%" }}>
            {dataCustomer.map((ele, index) => {
              const last = index === 4;
              return (
                <View fixed key={ele.initial} style={{ flexDirection: "row" }}>
                  <Text
                    style={{
                      fontSize: "6px",
                      textAlign: "center",
                      borderRight: BORDER,
                      borderLeft: BORDER,
                      borderTop: BORDER,
                      ...(last ? { borderBottom: BORDER } : {}),
                      paddingTop: "2px",
                      width: "6%",
                    }}
                  >
                    {index + 1}
                  </Text>
                  <Text
                    style={{
                      fontSize: "6px",
                      borderRight: BORDER,
                      borderTop: BORDER,
                      ...(last ? { borderBottom: BORDER } : {}),
                      padding: "2px 0 0 2px",
                      width: "60%",
                    }}
                  >
                    {ele.customer}
                  </Text>
                  <Text
                    style={{
                      fontSize: "6px",
                      textAlign: "center",
                      borderTop: BORDER,
                      borderRight: BORDER,
                      ...(last ? { borderBottom: BORDER } : {}),
                      paddingTop: "2px",
                      width: "8%",
                    }}
                  >
                    {ele.initial}
                  </Text>
                </View>
              );
            })}
          </View>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              border: BORDER,
              width: "46%",
            }}
          >
            <View style={{ borderRight: BORDER, width: "41%" }}>
              <Text
                style={{
                  textAlign: "center",
                  borderBottom: BORDER,
                  fontSize: "8px",
                }}
              >
                DITERIMA
              </Text>
              <Text style={{ height: "26px" }} />
              <Text
                style={{
                  textAlign: "center",
                  borderTop: BORDER,
                  fontSize: "8px",
                }}
              >
                QC
              </Text>
            </View>
            <View style={{ width: "59%" }}>
              <Text
                style={{
                  textAlign: "center",
                  borderBottom: BORDER,
                  fontSize: "8px",
                }}
              >
                DIBUAT
              </Text>
              <Text style={{ height: "26px" }} />
              <Text
                style={{
                  textAlign: "center",
                  borderTop: BORDER,
                  fontSize: "8px",
                }}
              >
                GUDANG
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PdfMemoKirim;
